import { useEffect, useState } from 'react';
import { includes, isEmpty } from 'lodash';

import { UsersContextType } from '@/operator/components/users/context/UsersContext';
import Groups2OutlinedIcon from '@mui/icons-material/GroupOutlined';
import { HeaderWithIconProps } from '@/operator/components/general/header/HeaderWithIcon';
import { UsersViewTitles } from '@/operator/components/users/constant/UsersViewTitles.enum';
import {
    FirestoreUser,
    Invitation,
    useGetInvitesQuery,
    useGetUsersQuery,
} from 'generated/graphql';
import { useAppSelector } from 'hooks/index';
import { selectPartnerData } from 'store/partner/partnerSelector';

const useUsersTable = (): UsersContextType => {
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState<Invitation[]>();

    const [usersTable, setUsersTable] = useState<FirestoreUser[]>();
    const [invitationsTable, setInvitationsTable] = useState<Invitation[]>();

    const [searchInput, setSearchInput] = useState<string>('');
    const [searchFilters, setSearchFilters] = useState([]);

    const [header, setHeader] = useState<HeaderWithIconProps>({
        title: UsersViewTitles.Users,
        Icon: Groups2OutlinedIcon,
    });

    const { instanceId } = useAppSelector(selectPartnerData);

    const { data: queryUsers, loading: loadingUsers } = useGetUsersQuery({
        variables: {
            instanceId,
        },
    });

    const { data: queryInvites, loading: loadingInvites } = useGetInvitesQuery({
        variables: {
            organizationId: instanceId,
        },
    });

    const usersResults = queryUsers?.users || [];
    const invitesResults = queryInvites?.invitations || [];

    useEffect(() => {
        setUsers(usersResults);
    }, [usersResults]);

    useEffect(() => {
        setInvitations(invitesResults);
    }, [invitesResults]);

    const removeUser = (userId: string) => {
        const filteredUsers = users.filter((user) => user.id !== userId);
        setUsers(filteredUsers);
    };

    const removeInvitation = (invitationId: string) => {
        const filteredUsers = invitations.filter(
            (invitation) => invitation.id !== invitationId,
        );
        setInvitations(filteredUsers);
    };

    const getFilteredUserTable = (chips: string[]): FirestoreUser[] => {
        if (isEmpty(chips)) {
            return users;
        }

        const filteredUsers: FirestoreUser[] = [];
        users.forEach((user) => {
            chips.forEach((chip) => {
                if (
                    includes(
                        user.firstName.toLowerCase(),
                        chip.toLowerCase(),
                    ) ||
                    includes(user.lastName.toLowerCase(), chip.toLowerCase()) ||
                    includes(user.email.toLowerCase(), chip.toLowerCase())
                ) {
                    filteredUsers.push(user);
                }
            });
        });
        return filteredUsers;
    };

    const getFilteredInvitationsTable = (chips: string[]): Invitation[] => {
        if (isEmpty(chips)) {
            return invitations;
        }

        const filteredInvitations: Invitation[] = [];
        invitations.forEach((invitation) => {
            chips.forEach((chip) => {
                if (
                    includes(invitation.email.toLowerCase(), chip.toLowerCase())
                ) {
                    filteredInvitations.push(invitation);
                }
            });
        });
        return filteredInvitations;
    };

    const handleOnSearchBarChange = (chips: string[]) => {
        const filteredUsers = getFilteredUserTable(chips);
        const filteredInvitations = getFilteredInvitationsTable(chips);
        setSearchFilters(chips);
        setUsersTable(filteredUsers);
        setInvitationsTable(filteredInvitations);
    };

    const addInvitation = (newInvitation: Invitation[]) => {
        setInvitations((prev) => [...prev, ...newInvitation]);
    };

    useEffect(() => {
        const usersFilteredBySearch = getFilteredUserTable(searchFilters);
        const invitationsFilteredBySearch =
            getFilteredInvitationsTable(searchFilters);

        setUsersTable(usersFilteredBySearch);
        setInvitationsTable(invitationsFilteredBySearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilters]);

    return {
        removeUser,
        removeInvitation,
        searchFilters,
        searchInput,
        setSearchInput,
        handleOnSearchBarChange,
        usersTable,
        invitationsTable,
        users,
        invitations,
        header,
        setHeader,
        addInvitation,
        loadingUsers,
        loadingInvites,
    };
};

export default useUsersTable;
